import React from "react";
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { useSmoothLoading } from "lib/use-smooth-loading";
import { Cell, CustomerHealthRow, SortField, SortDir } from "../api/api";

interface Props {
    data: CustomerHealthRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: SortField;
    sortOrder: SortDir;
    onSortChange: (col: SortField) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

type Column = {
    key: keyof CustomerHealthRow | "customer";
    label: string;
    sortField: SortField;
    isNumeric?: boolean;
};

const columns: Column[] = [
    { key: "customer", label: "Customer", sortField: "customer_name" },
    { key: "mrr", label: "MRR", sortField: "mrr", isNumeric: true },
    { key: "cm_pct", label: "CM%", sortField: "cm_pct", isNumeric: true },
    { key: "overdue_gt_60d", label: "Overdue >60d", sortField: "overdue_gt_60d", isNumeric: true },
    { key: "unbilled_excl_stock", label: "Unbilled (excl. stock)", sortField: "unbilled_excl_stock", isNumeric: true },
    { key: "fragmentation", label: "Fragmentation", sortField: "long_tail_share", isNumeric: true },
    { key: "red_count", label: "Red flags", sortField: "red_count", isNumeric: true },
];

const cellClasses = (cell: Cell): string => {
    if (cell.color === "RED") return "bg-red-50 text-red-700";
    return "bg-green-50 text-green-800";
};

const HealthTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const { isLoading } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const renderSkeletonRows = () =>
        Array.from({ length: 12 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    return (
        <div className="flex flex-col h-full">
            <div className="border border-gray-200 overflow-auto flex-1">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const active = sortBy === col.sortField;
                                return (
                                    <th
                                        key={col.key}
                                        onClick={() => onSortChange(col.sortField)}
                                        className={
                                            "border border-gray-300 px-2 py-2 cursor-pointer whitespace-nowrap font-semibold " +
                                            (col.isNumeric ? "text-right" : "text-left")
                                        }
                                    >
                                        <div
                                            className={
                                                "flex items-center gap-1 " +
                                                (col.isNumeric ? "justify-end" : "justify-start")
                                            }
                                        >
                                            {col.label}
                                            {active ? (
                                                sortOrder === "asc" ? (
                                                    <ChevronUp size={14} />
                                                ) : (
                                                    <ChevronDown size={14} />
                                                )
                                            ) : (
                                                <ChevronUp size={14} className="text-gray-300" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            renderSkeletonRows()
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-16 text-gray-500">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            data.map((row) => {
                                const nameMissing = row.customer_name === row.customer_code;
                                return (
                                    <tr key={row.customer_code} className="hover:bg-gray-50">
                                        <td
                                            className={
                                                "border border-gray-200 px-2 py-1.5 whitespace-nowrap text-left " +
                                                (nameMissing ? "text-gray-500 italic" : "text-gray-900")
                                            }
                                        >
                                            {row.customer_name}
                                        </td>
                                        <td className={"border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right " + cellClasses(row.mrr)}>
                                            {row.mrr.display}
                                        </td>
                                        <td className={"border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right " + cellClasses(row.cm_pct)}>
                                            {row.cm_pct.display}
                                        </td>
                                        <td className={"border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right " + cellClasses(row.overdue_gt_60d)}>
                                            {row.overdue_gt_60d.display}
                                        </td>
                                        <td className={"border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right " + cellClasses(row.unbilled_excl_stock)}>
                                            {row.unbilled_excl_stock.display}
                                        </td>
                                        <td className={"border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right " + cellClasses(row.fragmentation)}>
                                            {row.fragmentation.display}
                                        </td>
                                        <td className="border border-gray-200 px-2 py-1.5 whitespace-nowrap text-right">
                                            {row.red_count > 0 ? (
                                                <span className="inline-flex items-center justify-center min-w-[1.5rem] bg-red-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                                                    {row.red_count}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">0</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between pt-4">
                <span className="text-sm text-gray-600">
                    Showing{" "}
                    <strong>{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</strong>
                    {" "}to{" "}
                    <strong>{Math.min(page * pageSize, totalItems)}</strong>
                    {" "}of <strong>{totalItems}</strong>
                </span>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm">
                        Rows per page
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="border rounded px-2 py-1"
                        >
                            {[10, 20, 50, 100].map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            disabled={page === 1}
                            onClick={() => onPageChange(page - 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className="px-2 text-sm">
                            Page <strong>{page}</strong> / {totalPages}
                        </span>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(page + 1)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            disabled={page === totalPages}
                            onClick={() => onPageChange(totalPages)}
                            className="p-1 border rounded disabled:opacity-50"
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HealthTable;
